import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  adminNavBackUrlAtom,
  adminNavBarUseAtom,
  adminNavBarUseNavLeftAtom,
  adminNavRightTextAtom,
  adminNavTitleAtom,
} from '@service/bugo/state';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { SidePopup } from '@shared/components/SidePopup';
import {
  releaseNoteChannelsOfRole,
  useReleaseNotes,
} from '@shared/releaseNotes/useReleaseNotes';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export const AdminNavBar: FC = function AdminNavBar() {
  const { Back, ChevronRight, EmptyUser, User, Menu } = Icon();
  const openId = 'adminNavSidePopup';
  const setSideMenuOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  const location = useLocation();
  const pathName = location.pathname;
  const navigate = useNavigate();
  const title = useRecoilValue(adminNavTitleAtom);
  const rightText = useRecoilValue(adminNavRightTextAtom);
  const navBarUse = useRecoilValue(adminNavBarUseAtom);
  const navLeftUse = useRecoilValue(adminNavBarUseNavLeftAtom);
  const backUrl = useRecoilValue(adminNavBackUrlAtom);

  const { userProfile, apiLogout, isAuthenticated, isAdmin, isBugoAgencyAdmin, roles } =
    useAuth();

  //* 안 읽은 패치노트가 있으면 메뉴에 점을 찍는다 (읽음 여부는 기기별 localStorage)
  const releaseNoteChannels = useMemo(() => releaseNoteChannelsOfRole(roles), [roles]);
  const { hasUnread: hasUnreadReleaseNote } = useReleaseNotes(releaseNoteChannels);
  const bugoBrandId = userProfile?.bugoAgencyAdminDetail?.bugoBrands
    ? userProfile?.bugoAgencyAdminDetail?.bugoBrands[0]?._id
    : 'undefined';
  useEffect(() => {
    return setSideMenuOpen(false);
  }, [setSideMenuOpen]);

  const menuButton = (text: string, to: string) => {
    return (
      <Link
        to={to}
        className="flex h-10 w-full items-center justify-between pl-1"
        onClick={() => {
          setSideMenuOpen(false);
        }}
      >
        <p className="bugo-h2">{text}</p>
        <ChevronRight />
      </Link>
    );
  };

  //* menuButton과 같은 모양에 안 읽음 점만 얹은 변형
  const menuButtonWithDot = (text: string, to: string, showDot: boolean) => {
    return (
      <Link
        to={to}
        className="flex h-10 w-full items-center justify-between pl-1"
        onClick={() => {
          setSideMenuOpen(false);
        }}
      >
        <div className="flex items-center gap-1.5">
          <p className="bugo-h2">{text}</p>
          {showDot && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
        </div>
        <ChevronRight />
      </Link>
    );
  };

  const bugoSideMenu = (login: boolean) => {
    const sharedMenu = (
      <>
        <div>
          <div className="px-4 py-0.5">
            <p className="text-xs font-bold leading-5 theme-text-8">관리</p>
          </div>
          <div className="space-y-2 px-3 py-2 theme-bg-1">
            {menuButton('장례식장정보', `funeral-home-info/manage`)}
            {menuButton('상조관리', `bugo-brand`)}
            {menuButton('DN관리', `distribution-network`)}
            {menuButton('문의관리', `enquiry`)}
            {menuButton('계정관리', `users`)}
            {/* 배포별 패치노트 — 지금은 Admin만 (docs/releases/README.md) */}
            {isAdmin() &&
              menuButtonWithDot(
                '업데이트 소식',
                '/admin/release-notes',
                hasUnreadReleaseNote,
              )}
          </div>
        </div>
        {(isBugoAgencyAdmin() || isAdmin()) && (
          <div>
            <div className="px-4 py-0.5">
              <p className="text-xs font-bold leading-5 theme-text-8">공지사항</p>
            </div>
            <div className="space-y-2 px-3 py-2 theme-bg-1">
              {menuButton('공지사항 등록', '/bugo-brand/announcement/add')}
              {menuButton('공지사항 관리', '/bugo-brand/announcement/manage')}
            </div>
          </div>
        )}
        {!isBugoAgencyAdmin() && (
          <div>
            <div className="px-4 py-0.5">
              <p className="text-xs font-bold leading-5 theme-text-8">부고메뉴</p>
            </div>
            <div className="space-y-2 px-3 py-2 theme-bg-1">
              {menuButton('부고 등록', '/fevent/add')}
              {menuButton('부고 관리', '/fevent/manage')}
              {isAdmin() && menuButton('답례 관리', '/admin/courtesy-manage')}
            </div>
          </div>
        )}
        {!isBugoAgencyAdmin() && (
          <div>
            <div className="px-4 py-0.5">
              <p className="text-xs font-bold leading-5 theme-text-8">화환메뉴</p>
            </div>
            <div className="space-y-2 px-3 py-2 theme-bg-1">
              {menuButton('화환 보내기', '/flower-store')}
              {/* //TODO: 종합버전에서 살리기 */}
              {/* {menuButton('답례품 보내기', '/flower-store')} */}
            </div>
          </div>
        )}

        <div>
          <div className="px-4 py-0.5">
            <p className="text-xs font-bold leading-5 theme-text-8">고객센터</p>
          </div>
          <div className="space-y-2 px-3 py-2 theme-bg-1">
            {menuButton('자주 묻는 질문', '/faq')}
            {menuButton('문의하기', '/enquiry')}
            {menuButton('내 문의내역', '/my-enquiries')}
          </div>
        </div>
      </>
    );

    if (login) {
      return (
        <div className="relative min-h-screen w-64 theme-text-1 theme-bg-6">
          <div className="absolute right-0 top-0"></div>
          <div className="flex items-center justify-between pl-3 pr-2 pt-10 pb-4 theme-bg-1">
            <div className="flex items-center gap-2">
              <User />
              <p className="bugo-h1">{userProfile?.info?.name}님</p>
            </div>
            <ChevronRight />
          </div>
          {/* <div>
              <div className="px-4 py-0.5">
                <p className="text-xs font-bold leading-5 theme-text-8">마이페이지</p>
              </div>
              <div className="space-y-2 px-3 py-2 theme-bg-1">
                {menuButton('적립금 관리', '/fevent/add')}
                {menuButton('나를 추천한 회원', '/fevent/manage')}
              </div>
            </div> */}
          {sharedMenu}
          <div className="flex items-center justify-end p-4">
            <Button
              className="button-fold text-xs leading-5 theme-text-8 theme-bg-1 theme-border-1"
              onClick={() => {
                apiLogout();
              }}
            >
              로그아웃
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="min-h-screen w-64 theme-text-1 theme-bg-6">
          <Link to="/login">
            <div className="flex items-center justify-between pl-3 pr-2 pt-10 pb-4 theme-bg-1">
              <div className="flex items-center gap-2">
                <EmptyUser />
                <p className="bugo-h1">로그인</p>
              </div>
              <ChevronRight />
            </div>
          </Link>
          {sharedMenu}
        </div>
      );
    }
  };

  const navLeft = () => {
    if (pathName === '/') {
      return (
        <Link to="/">
          관리자모드
          {/* <img src={} className="h-9"></img> */}
        </Link>
      );
    } else {
      return (
        <div className="flex items-center">
          {navLeftUse && (
            <button
              onClick={() => {
                backUrl ? navigate(backUrl) : navigate(-1);
              }}
              className="center-box wh-10"
            >
              <Back />
            </button>
          )}
          <p className="bugo-h1">{title}</p>
        </div>
      );
    }
  };

  const navRight = () => {
    if (rightText === '') {
      return (
        <div
          className="center-box wh-10 cursor-pointer"
          onClick={() => setSideMenuOpen(true)}
        >
          <Menu />
        </div>
      );
    } else {
      return (
        <div>
          <p className="bugo-h2 cursor-pointer theme-text-main">{rightText}</p>
        </div>
      );
    }
  };

  if (pathName.endsWith('login') || pathName.endsWith('error') || !navBarUse) {
    return null;
  } else {
    return (
      <>
        <div className="fixed z-20 flex h-14 w-full max-w-inherit items-center justify-between px-4 theme-bg-2">
          {navLeft()}
          {navRight()}
        </div>
        <SidePopup openId={openId}>{bugoSideMenu(isAuthenticated)}</SidePopup>
      </>
    );
  }
};
